/**
 * Visual dos avisos da plataforma — usado tanto na prévia do /admin quanto
 * na tela real do cliente. Quatro formatos:
 *   banner → faixa flutuante fixa no topo ou no rodapé da tela
 *   toast  → aviso discreto em um dos quatro cantos
 *   modal  → janela central, com fundo escuro, em 3 larguras
 *   post   → cartão em um dos quatro cantos, estilo mensagem da equipe
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Angry,
  Bell,
  CalendarClock,
  CheckCircle2,
  Frown,
  Gift,
  Info,
  Laugh,
  Megaphone,
  Meh,
  PartyPopper,
  Rocket,
  ShieldCheck,
  Smile,
  Sparkles,
  Volume2,
  Wrench,
  X,
  Zap,
} from "lucide-react";

/** Botão que o usuário toca para ligar o som. O navegador só libera áudio
 *  depois de um clique: por isso o vídeo com autoplay começa sem som. */
const BotaoSom = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm transition-colors hover:bg-black/85"
    data-testid="aviso-video-som"
  >
    <Volume2 size={13} /> Ativar som
  </button>
);

export const ICONES = {
  megafone: { label: "Megafone", icone: Megaphone },
  brilho: { label: "Novidade", icone: Sparkles },
  info: { label: "Informação", icone: Info },
  alerta: { label: "Atenção", icone: AlertTriangle },
  presente: { label: "Presente", icone: Gift },
  foguete: { label: "Lançamento", icone: Rocket },
  sino: { label: "Aviso", icone: Bell },
  ferramenta: { label: "Manutenção", icone: Wrench },
  agenda: { label: "Agenda", icone: CalendarClock },
  festa: { label: "Comemoração", icone: PartyPopper },
  escudo: { label: "Segurança", icone: ShieldCheck },
  raio: { label: "Rápido", icone: Zap },
};

export const MODOS_IMAGEM = {
  topo: { label: "Só no topo", ajuda: "Acima do texto" },
  cheia: { label: "Ocupa tudo", ajuda: "Imagem inteira" },
};

export const TAMANHOS_MODAL = {
  pequeno: { label: "Pequeno", px: 420 },
  medio: { label: "Médio", px: 560 },
  grande: { label: "Grande", px: 720 },
};

export const POSICOES_BANNER = {
  topo: "No topo",
  rodape: "No rodapé",
};

export const POSICOES_CANTO = {
  "superior-esquerdo": "Superior esquerdo",
  "superior-direito": "Superior direito",
  "inferior-esquerdo": "Inferior esquerdo",
  "inferior-direito": "Inferior direito",
};

const IconeDo = ({ campanha, size = 16, className = "" }) => {
  const Ico = ICONES[campanha?.conteudo?.icone]?.icone || Megaphone;
  return <Ico size={size} className={className} />;
};

const Imagem = ({ url, className = "" }) =>
  url ? (
    <img
      src={url}
      alt=""
      className={`w-full object-cover ${className}`}
      data-testid="aviso-imagem"
    />
  ) : null;

/** Vídeo do YouTube ocupando o aviso inteiro (16:9). Sem texto por cima. */
export const idYoutube = (url) => {
  if (!url) return null;
  const achou = String(url).match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/,
  );
  return achou ? achou[1] : null;
};

/** O aviso tem vídeo para mostrar, seja link do YouTube ou arquivo enviado. */
export const temVideo = (c = {}) =>
  c.video_fonte === "upload" ? !!c.video_upload_url : !!idYoutube(c.video_url);

/** Vídeo enviado para o armazenamento da plataforma, tocado pelo próprio
 *  navegador. Reporta os mesmos marcos do YouTube (início, 25%, 50%, 75% e
 *  conclusão), para o relatório da campanha não mudar. */
const CorpoVideoArquivo = ({ campanha, onClose, onEvento }) => {
  const c = campanha.conteudo || {};
  const player = useRef(null);
  const [mudo, setMudo] = useState(!!c.video_autoplay);
  const estado = useRef({ iniciou: false, marcos: {}, segundos: 0, duracao: 0, ultimoEnvio: 0 });

  const enviar = useCallback(
    (tipo, extras = {}) => {
      const e = estado.current;
      onEvento?.({
        tipo,
        segundos: Math.round(e.segundos * 10) / 10,
        duracao: Math.round(e.duracao * 10) / 10,
        ...extras,
      });
    },
    [onEvento],
  );

  useEffect(() => {
    return () => {
      if (estado.current.iniciou) enviar("progress");
    };
  }, [enviar]);

  const aoTocar = () => {
    const e = estado.current;
    if (e.iniciou) return;
    e.iniciou = true;
    enviar("start", { automatico: !!c.video_autoplay });
  };

  const aoAndar = (ev) => {
    const e = estado.current;
    const el = ev.currentTarget;
    e.duracao = el.duration || e.duracao;
    e.segundos = Math.max(e.segundos, el.currentTime || 0);
    if (e.duracao > 0 && e.segundos > 0) {
      const pct = (e.segundos / e.duracao) * 100;
      for (const [marco, alvo] of [
        ["p25", 25],
        ["p50", 50],
        ["p75", 75],
      ]) {
        if (pct >= alvo && !e.marcos[marco]) {
          e.marcos[marco] = true;
          enviar(marco);
        }
      }
      if (e.segundos - e.ultimoEnvio >= 15) {
        e.ultimoEnvio = e.segundos;
        enviar("progress");
      }
    }
  };

  const aoTerminar = () => {
    const e = estado.current;
    if (e.marcos.complete) return;
    e.marcos.complete = true;
    enviar("complete");
  };

  if (!c.video_upload_url) return null;

  const ligarSom = () => {
    const el = player.current;
    if (!el) return;
    el.muted = false;
    el.volume = 1;
    setMudo(false);
    el.play?.().catch(() => {});
  };

  return (
    <div className="relative" data-testid="aviso-video">
      <div className="relative w-full bg-black pb-[56.25%]">
        <video
          ref={player}
          src={c.video_upload_url}
          className="absolute inset-0 h-full w-full"
          autoPlay={!!c.video_autoplay}
          muted={mudo}
          controls={!!c.video_controles}
          loop={!!c.video_loop}
          playsInline
          controlsList={c.video_tela_cheia ? undefined : "nofullscreen"}
          disablePictureInPicture={!c.video_tela_cheia}
          onPlay={aoTocar}
          onTimeUpdate={aoAndar}
          onEnded={aoTerminar}
          onVolumeChange={(ev) => setMudo(ev.currentTarget.muted)}
          data-testid="aviso-video-arquivo"
        />
      </div>
      {mudo && <BotaoSom onClick={ligarSom} />}
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar aviso"
        className="absolute right-2 top-2 rounded-full bg-black/55 p-1.5 text-white backdrop-blur-sm transition-colors hover:bg-black/80"
        data-testid="aviso-fechar"
      >
        <X size={14} />
      </button>
    </div>
  );
};

const CorpoVideoYoutube = ({ campanha, onClose, onEvento }) => {
  const c = campanha.conteudo || {};
  const id = idYoutube(c.video_url);
  const frame = useRef(null);
  const [mudo, setMudo] = useState(!!c.video_autoplay);
  const estado = useRef({ iniciou: false, marcos: {}, segundos: 0, duracao: 0, ultimoEnvio: 0 });

  const enviar = useCallback(
    (tipo, extras = {}) => {
      const e = estado.current;
      onEvento?.({
        tipo,
        segundos: Math.round(e.segundos * 10) / 10,
        duracao: Math.round(e.duracao * 10) / 10,
        ...extras,
      });
    },
    [onEvento],
  );

  // O play e o progresso acontecem dentro do iframe: ouvimos a API do YouTube.
  useEffect(() => {
    const aoReceber = (ev) => {
      if (!String(ev.origin).includes("youtube")) return;
      let d;
      try {
        d = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
      } catch {
        return;
      }
      const info = d?.info || {};
      const e = estado.current;
      if (info.duration) e.duracao = info.duration;
      if (typeof info.currentTime === "number") e.segundos = Math.max(e.segundos, info.currentTime);

      const tocando = info.playerState === 1 || (d?.event === "onStateChange" && d.info === 1);
      if (tocando && !e.iniciou) {
        e.iniciou = true;
        enviar("start", { automatico: !!c.video_autoplay });
        // Alguns navegadores ligam legenda sozinhos quando o vídeo está mudo.
        if (!c.video_legendas) {
          frame.current?.contentWindow?.postMessage(
            JSON.stringify({
              event: "command",
              func: "setOption",
              args: ["captions", "track", {}],
            }),
            "*",
          );
        }
      }
      if (info.playerState === 0 && !e.marcos.complete) {
        e.marcos.complete = true;
        enviar("complete");
      }
      if (e.duracao > 0 && e.segundos > 0) {
        const pct = (e.segundos / e.duracao) * 100;
        for (const [marco, alvo] of [
          ["p25", 25],
          ["p50", 50],
          ["p75", 75],
        ]) {
          if (pct >= alvo && !e.marcos[marco]) {
            e.marcos[marco] = true;
            enviar(marco);
          }
        }
        // Tempo assistido: um ping leve a cada 15s, pra não perder se fechar.
        if (e.segundos - e.ultimoEnvio >= 15) {
          e.ultimoEnvio = e.segundos;
          enviar("progress");
        }
      }
    };
    window.addEventListener("message", aoReceber);
    return () => {
      window.removeEventListener("message", aoReceber);
      if (estado.current.iniciou) enviar("progress");
    };
  }, [enviar, c.video_autoplay, c.video_legendas]);

  if (!id) return null;
  const params = new URLSearchParams({
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
    enablejsapi: "1",
    controls: c.video_controles ? "1" : "0",
    fs: c.video_tela_cheia ? "1" : "0",
    cc_load_policy: c.video_legendas ? "1" : "0",
    iv_load_policy: "3",
    ...(c.video_loop ? { loop: "1", playlist: id } : {}),
    ...(c.video_autoplay ? { autoplay: "1", mute: "1" } : {}),
  });

  const aoCarregar = () => {
    frame.current?.contentWindow?.postMessage(
      JSON.stringify({ event: "listening", id: 1, channel: "widget" }),
      "*",
    );
  };

  const comando = (func, args = []) =>
    frame.current?.contentWindow?.postMessage(
      JSON.stringify({ event: "command", func, args }),
      "*",
    );

  const ligarSom = () => {
    comando("unMute");
    comando("setVolume", [100]);
    comando("playVideo");
    setMudo(false);
  };

  return (
    <div className="relative" data-testid="aviso-video">
      <div className="relative w-full bg-black pb-[56.25%]">
        <iframe
          ref={frame}
          src={`https://www.youtube.com/embed/${id}?${params}`}
          title="Vídeo"
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
          allowFullScreen
          onLoad={aoCarregar}
          data-testid="aviso-video-frame"
        />
      </div>
      {mudo && <BotaoSom onClick={ligarSom} />}
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar aviso"
        className="absolute right-2 top-2 rounded-full bg-black/55 p-1.5 text-white backdrop-blur-sm transition-colors hover:bg-black/80"
        data-testid="aviso-fechar"
      >
        <X size={14} />
      </button>
    </div>
  );
};

/** Escolhe o player conforme a origem do vídeo da campanha. */
const CorpoVideo = (props) =>
  (props.campanha?.conteudo || {}).video_fonte === "upload" ? (
    <CorpoVideoArquivo {...props} />
  ) : (
    <CorpoVideoYoutube {...props} />
  );

/** Imagem ocupando o aviso inteiro: X sobreposto e texto sobre o degradê. */
const CorpoImagemCheia = ({ campanha, onClose, onClick }) => {
  const c = campanha.conteudo || {};
  const temTexto = c.titulo || c.texto || c.cta_label;
  return (
    <div className="relative" data-testid="aviso-imagem-cheia">
      <img src={c.imagem_url} alt="" className="block w-full object-cover" />
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar aviso"
        className="absolute right-3 top-3 rounded-full bg-black/45 p-1.5 text-white backdrop-blur-sm transition-colors hover:bg-black/65"
        data-testid="aviso-fechar"
      >
        <X size={14} />
      </button>
      {temTexto && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/45 to-transparent p-5 pt-10">
          {c.titulo && <p className="text-base font-semibold text-white">{c.titulo}</p>}
          {c.texto && (
            <p className="mt-1 whitespace-pre-line text-xs leading-relaxed text-white/85">
              {c.texto}
            </p>
          )}
          {c.cta_label && (
            <div className="mt-3">
              <Cta campanha={campanha} onClick={onClick} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Cta = ({ campanha, onClick, estilo = "solid" }) => {
  const { cta_label: label, cor } = campanha.conteudo || {};
  if (!label) return null;
  const base =
    "inline-flex h-8 items-center rounded-full px-3.5 text-xs font-semibold transition-transform active:scale-[0.97]";
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        estilo === "ghost"
          ? `${base} bg-white/20 text-white hover:bg-white/30`
          : `${base} text-white hover:brightness-110`
      }
      style={estilo === "ghost" ? undefined : { backgroundColor: cor || "#00AFE5" }}
      data-testid="aviso-cta"
    >
      {label}
    </button>
  );
};

const Fechar = ({ onClose, claro = false }) => (
  <button
    type="button"
    onClick={onClose}
    aria-label="Fechar aviso"
    className={`shrink-0 rounded-md p-1 transition-colors ${
      claro
        ? "text-white/70 hover:bg-white/15 hover:text-white"
        : "text-gray-400 hover:bg-gray-100 hover:text-gray-700"
    }`}
    data-testid="aviso-fechar"
  >
    <X size={14} />
  </button>
);

export const AvisoBanner = ({ campanha, onClose, onClick }) => {
  const c = campanha.conteudo || {};
  return (
    <div
      className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-white shadow-2xl ring-1 ring-black/5"
      style={{ backgroundColor: c.cor || "#00AFE5" }}
      data-testid="aviso-banner"
    >
      <IconeDo campanha={campanha} className="shrink-0 opacity-90" />
      <div className="min-w-0 flex-1">
        {c.titulo && (
          <p className="break-words text-sm font-semibold leading-tight">{c.titulo}</p>
        )}
        {/* O texto quebra linha ao chegar no fim do banner em vez de cortar
            com reticências, e respeita as quebras digitadas na campanha. */}
        {c.texto && (
          <p className="whitespace-pre-line break-words text-xs leading-snug text-white/85">
            {c.texto}
          </p>
        )}
      </div>
      <Cta campanha={campanha} onClick={onClick} estilo="ghost" />
      <Fechar onClose={onClose} claro />
    </div>
  );
};

export const AvisoToast = ({ campanha, onClose, onClick }) => {
  const c = campanha.conteudo || {};
  return (
    <div
      className="w-[22rem] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
      data-testid="aviso-toast"
    >
      <div className="h-1" style={{ backgroundColor: c.cor || "#00AFE5" }} />
      <div className="flex items-start gap-3 p-3.5">
        <div
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white"
          style={{ backgroundColor: c.cor || "#00AFE5" }}
        >
          <IconeDo campanha={campanha} size={14} />
        </div>
        <div className="min-w-0 flex-1">
          {c.titulo && <p className="text-sm font-semibold text-gray-900">{c.titulo}</p>}
          {c.texto && <p className="mt-0.5 text-xs leading-relaxed text-gray-600">{c.texto}</p>}
          {c.cta_label && (
            <div className="mt-2.5">
              <Cta campanha={campanha} onClick={onClick} />
            </div>
          )}
        </div>
        <Fechar onClose={onClose} />
      </div>
    </div>
  );
};

export const AvisoModal = ({ campanha, onClose, onClick, onVideoEvento }) => {
  const c = campanha.conteudo || {};
  const px = TAMANHOS_MODAL[campanha.exibicao?.tamanho]?.px || TAMANHOS_MODAL.medio.px;
  const video = c.midia_tipo === "video" && temVideo(c);
  const cheia = c.imagem_url && c.imagem_modo === "cheia";
  return (
    <div
      className="w-full overflow-hidden rounded-2xl bg-white shadow-2xl"
      style={{ maxWidth: px }}
      data-testid="aviso-modal"
    >
      {video ? (
        <CorpoVideo campanha={campanha} onClose={onClose} onEvento={onVideoEvento} />
      ) : cheia ? (
        <CorpoImagemCheia campanha={campanha} onClose={onClose} onClick={onClick} />
      ) : (
      <>
      <Imagem url={c.imagem_url} className="max-h-44" />
      <div className="relative p-6">
        <div className="absolute right-3 top-3">
          <Fechar onClose={onClose} />
        </div>
        <div
          className="flex h-9 w-9 items-center justify-center rounded-xl text-white"
          style={{ backgroundColor: c.cor || "#00AFE5" }}
        >
          <IconeDo campanha={campanha} />
        </div>
        {c.titulo && (
          <h2 className="mt-3.5 text-lg font-semibold leading-snug text-gray-900">{c.titulo}</h2>
        )}
        {c.texto && (
          <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-600">
            {c.texto}
          </p>
        )}
        {/* Sem botão de ação, o X já resolve: nada de "Agora não" sozinho. */}
        {c.cta_label && (
          <div className="mt-5 flex items-center gap-2">
            <Cta campanha={campanha} onClick={onClick} />
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 items-center rounded-full px-3 text-xs font-medium text-gray-500 hover:bg-gray-100"
              data-testid="aviso-modal-depois"
            >
              Agora não
            </button>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export const AvisoPost = ({ campanha, onClose, onClick, onVideoEvento }) => {
  const c = campanha.conteudo || {};
  if (c.midia_tipo === "video" && temVideo(c)) {
    return (
      <div
        className="w-[26rem] max-w-[92vw] overflow-hidden rounded-2xl bg-white shadow-2xl"
        data-testid="aviso-post"
      >
        <CorpoVideo campanha={campanha} onClose={onClose} onEvento={onVideoEvento} />
      </div>
    );
  }
  if (c.imagem_url && c.imagem_modo === "cheia") {
    return (
      <div
        className="w-[23rem] overflow-hidden rounded-2xl bg-white shadow-2xl"
        data-testid="aviso-post"
      >
        <CorpoImagemCheia campanha={campanha} onClose={onClose} onClick={onClick} />
      </div>
    );
  }
  return (
    <div
      className="w-[23rem] overflow-hidden rounded-2xl bg-white shadow-2xl"
      data-testid="aviso-post"
    >
      <div
        className="flex items-center gap-2 px-4 py-2.5 text-white"
        style={{ backgroundColor: c.cor || "#00AFE5" }}
      >
        <IconeDo campanha={campanha} size={14} className="shrink-0" />
        <span className="truncate text-sm font-semibold">{c.titulo || "Aviso"}</span>
        <span className="ml-auto">
          <Fechar onClose={onClose} claro />
        </span>
      </div>
      <Imagem url={c.imagem_url} className="max-h-36" />
      <div className="p-4">
        {c.texto && (
          <p className="whitespace-pre-line text-xs leading-relaxed text-gray-600">
            {c.texto}
          </p>
        )}
        {c.cta_label && (
          <div className="mt-3">
            <Cta campanha={campanha} onClick={onClick} />
          </div>
        )}
      </div>
    </div>
  );
};

/* ───────────────────────── Pesquisa (CSAT / NPS) ───────────────────────── */

export const TIPOS_PESQUISA = {
  csat: {
    label: "CSAT",
    ajuda: "Satisfação de 1 a 5, com carinhas",
    escala: [1, 5],
    pergunta: "Como você avalia sua experiência com o sistema?",
  },
  nps: {
    label: "NPS",
    ajuda: "Recomendação de 0 a 10",
    escala: [0, 10],
    pergunta: "De 0 a 10, o quanto você recomendaria o sistema a um colega?",
  },
};

export const APRESENTACOES_PESQUISA = {
  canto: { label: "No canto", ajuda: "Discreto, sem travar a tela" },
  modal: { label: "No centro", ajuda: "Janela com fundo escuro" },
};

const CARINHAS = [
  { nota: 1, icone: Angry, label: "Muito ruim", cor: "#ef4444" },
  { nota: 2, icone: Frown, label: "Ruim", cor: "#f97316" },
  { nota: 3, icone: Meh, label: "Regular", cor: "#f59e0b" },
  { nota: 4, icone: Smile, label: "Boa", cor: "#22c55e" },
  { nota: 5, icone: Laugh, label: "Ótima", cor: "#10b981" },
];

const corNps = (n) => (n <= 6 ? "#ef4444" : n <= 8 ? "#f59e0b" : "#10b981");

const regraDaNota = (regras, nota) =>
  (regras || []).find((r) => nota >= Number(r.de) && nota <= Number(r.ate)) || null;

export const AvisoPesquisa = ({ campanha, onClose, onResponder }) => {
  const c = campanha.conteudo || {};
  const p = campanha.pesquisa || {};
  const tipo = p.tipo === "nps" ? "nps" : "csat";
  const cor = c.cor || "#00AFE5";
  const pergunta = p.pergunta || TIPOS_PESQUISA[tipo].pergunta;
  const amplo = p.apresentacao === "modal";
  // "Até que responda": sem X — a pesquisa só sai da tela depois da nota.
  const semFechar = (campanha.exibicao || {}).frequencia === "ate_responder";

  const [nota, setNota] = useState(null);
  const [comentario, setComentario] = useState("");
  const [etapa, setEtapa] = useState("nota");
  const regra = nota == null ? null : regraDaNota(p.comentario_regras, nota);

  const enviar = (n, texto) => {
    onResponder?.(n, texto);
    setEtapa("obrigado");
    setTimeout(() => onClose?.(), 2600);
  };

  const escolher = (n) => {
    setNota(n);
    if (regraDaNota(p.comentario_regras, n)) setEtapa("comentario");
    else enviar(n, "");
  };

  return (
    <div
      className={`overflow-hidden rounded-2xl bg-white shadow-2xl ${
        amplo ? "w-[30rem] max-w-[92vw]" : tipo === "nps" ? "w-[26rem] max-w-[92vw]" : "w-[24rem] max-w-[92vw]"
      }`}
      data-testid="aviso-pesquisa"
    >
      <div className="h-1" style={{ backgroundColor: cor }} />
      {etapa === "obrigado" ? (
        <div
          className="flex flex-col items-center gap-2.5 px-6 py-7 text-center"
          data-testid="pesquisa-obrigado"
        >
          <CheckCircle2 size={34} style={{ color: cor }} />
          <p className="text-sm font-semibold text-gray-900">
            {p.agradecimento || "Obrigado pela sua resposta!"}
          </p>
        </div>
      ) : (
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div
            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white"
            style={{ backgroundColor: cor }}
          >
            <IconeDo campanha={campanha} size={14} />
          </div>
          <div className="min-w-0 flex-1">
            {c.titulo && (
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {c.titulo}
              </p>
            )}
            <p className="text-sm font-semibold leading-snug text-gray-900">
              {etapa === "comentario"
                ? regra?.pergunta || "Quer contar o motivo?"
                : pergunta}
            </p>
            {etapa === "nota" && c.texto && (
              <p className="mt-1 text-xs leading-relaxed text-gray-500">{c.texto}</p>
            )}
          </div>
          {!semFechar && <Fechar onClose={onClose} />}
        </div>

        {etapa === "nota" && tipo === "csat" && (
          <div className="mt-4 flex items-end justify-between gap-1">
            {CARINHAS.map((f) => {
              const Ico = f.icone;
              return (
                <button
                  key={f.nota}
                  type="button"
                  onClick={() => escolher(f.nota)}
                  className="group flex flex-1 flex-col items-center gap-1 rounded-lg py-2 transition-colors hover:bg-gray-50"
                  data-testid={`pesquisa-nota-${f.nota}`}
                >
                  <Ico
                    size={26}
                    className="text-gray-300 transition-transform group-hover:scale-110"
                    style={{ color: f.cor }}
                  />
                  <span className="text-[10px] leading-tight text-gray-500">{f.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {etapa === "nota" && tipo === "nps" && (
          <div className="mt-4">
            <div className="flex items-center gap-1">
              {Array.from({ length: 11 }, (_, n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => escolher(n)}
                  className="h-8 min-w-0 flex-1 rounded-lg border text-[11px] font-semibold text-gray-600 transition-all hover:-translate-y-0.5 hover:text-white"
                  style={{ borderColor: corNps(n) }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = corNps(n);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                  data-testid={`pesquisa-nota-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="mt-1.5 flex justify-between text-[10px] text-gray-400">
              <span>Não recomendaria</span>
              <span>Recomendaria muito</span>
            </div>
          </div>
        )}

        {etapa === "comentario" && (
          <div className="mt-3">
            <textarea
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              rows={3}
              autoFocus
              placeholder="Escreva aqui (opcional)"
              className="w-full resize-none rounded-lg border border-gray-200 p-2.5 text-xs text-gray-700 outline-none focus:border-gray-400"
              data-testid="pesquisa-comentario"
            />
            <div className="mt-2 flex items-center justify-end gap-2">
              {!regra?.obrigatorio && (
                <button
                  type="button"
                  onClick={() => enviar(nota, "")}
                  className="inline-flex h-8 items-center rounded-full px-3 text-xs font-medium text-gray-500 hover:bg-gray-100"
                  data-testid="pesquisa-pular"
                >
                  Pular
                </button>
              )}
              <button
                type="button"
                disabled={regra?.obrigatorio && !comentario.trim()}
                onClick={() => enviar(nota, comentario)}
                className="inline-flex h-8 items-center rounded-full px-3.5 text-xs font-semibold text-white transition-transform active:scale-[0.97] disabled:opacity-50"
                style={{ backgroundColor: cor }}
                data-testid="pesquisa-enviar"
              >
                Enviar
              </button>
            </div>
          </div>
        )}

      </div>
      )}
    </div>
  );
};

export const AVISO_POR_FORMATO = {
  banner: AvisoBanner,
  toast: AvisoToast,
  modal: AvisoModal,
  post: AvisoPost,
  pesquisa: AvisoPesquisa,
};
